import React, { Component } from 'react';
import { connect } from 'react-redux';
import BaseTable from 'react-base-table';
import AutoSizer from 'react-virtualized-auto-sizer';
import { __, setTableParams } from '../../core/utils';
import { createSidebar } from '../../redux/actions/general/sidebar';
import { listActiveModules } from '../../redux/actions/general/active_modules';
import { setPlusActions } from '../../redux/actions/general/plus';
import { setNotification } from '../../redux/actions/general/notification';
import {
  hideAlertbox,
  showAlertbox,
} from '../../redux/actions/general/alertbox';
import { hideDropdown } from '../../redux/actions/general/dropdown';
import {
  deleteCompanyTag,
  listCompanyTags,
} from '../../redux/actions/table/company_tags';
import TitleBar from '../../containers/TitleBar';
import Dropdown from '../../components/Dropdown';
import Tooltip from '../../components/Tooltip';
import MenuHIcon from '../../assets/images/menu-h-15x15.svg';
import EditIcon from '../../assets/images/edit-16x16.svg';
import DeleteIcon from '../../assets/images/delete-15x15.svg';
import styles from '../../styles/views/company/CompanyTags.scss';

@connect((state) => ({
  auth: state.auth,
  companyTags: state.table.company_tags,
}))
class CompanyTags extends Component {
  componentDidMount() {
    listActiveModules();
    setPlusActions([
      {
        title: __('company-tags.button.create', {}, 'Create tag'),
        onClick: () =>
          setTableParams('company_tags', {
            editor: { name: '' },
          }),
      },
    ]);
    createSidebar({
      title: __('topbar.account.company-settings'),
      items: [
        {
          title: __('submenu.title.company-details'),
          link: () => '/company/details',
        },
        {
          title: __('submenu.title.offices'),
          link: () => '/company/offices',
        },
        {
          title: __('company-settings.submenu.title.brands'),
          link: () => '/company/brands',
        },
        {
          title: __('company-settings.submenu.title.disciplines'),
          link: () => '/company/disciplines',
        },
        {
          title: __('submenu.title.market'),
          link: () => '/company/market',
        },
        {
          title: __('submenu.title.company_holidays'),
          link: () => '/company/company_holidays',
        },
        {
          title: __('submenu.title.company_positions'),
          link: () => '/company/company_positions',
        },
        {
          title: __('submenu.title.company_segments'),
          link: () => '/company/company_segments',
        },
        {
          title: __('submenu.title.company_benefits'),
          link: () => '/company/company_benefits',
        },
        {
          title: __('submenu.title.company_folders'),
          link: () => '/company/company_folders',
        },
        {
          title: __('submenu.title.planning_templates'),
          link: () => '/company/planning-templates/stages',
        },
        {
          title: __('submenu.title.tags'),
          link: () => '/company/tags',
          active: true,
        },
        _.includes(
          this.props.auth?.permissions,
          'view.permissions.in.permission.manager',
        )
          ? {
              title: __('submenu.title.company_roles_and_permissions'),
              link: () => '/company/company_roles_and_permissions',
            }
          : null,
      ].filter(Boolean),
    });
    listCompanyTags();
  }

  componentWillUnmount() {
    setTableParams('company_tags', { editor: undefined });
  }

  editTag = (tag) => {
    setTableParams('company_tags', { editor: tag });
  };

  deleteTag = (tag) => {
    showAlertbox({
      color: 'red',
      title: __('general.alert.are-you-sure'),
      description: __(
        'company-tags.delete.description',
        {},
        'The tag will be removed from all active company projects and from every item where it is used.',
      ),
      buttons: [
        {
          color: 'lightGray',
          text: __('general.alert.no-close'),
          onClick: 'close',
        },
        {
          color: 'gray',
          text: __('general.alert.yes-delete'),
          onClick: () => {
            deleteCompanyTag(tag.id).then(
              () => {
                hideAlertbox();
                setNotification(
                  __('company-tags.notification.deleted', {}, 'Tag deleted.'),
                );
                listCompanyTags();
              },
              ({ response }) => {
                hideAlertbox();
                setNotification({
                  text:
                    response?.message ||
                    __(
                      'company-tags.notification.delete-failed',
                      {},
                      'The tag could not be deleted.',
                    ),
                  type: 'warning',
                });
              },
            );
          },
        },
      ],
    });
  };

  render() {
    const {
      data = [],
      loading = false,
      error = false,
    } = this.props.companyTags || {};
    const tags = data.map(({ id, name }) => ({ id, name }));

    const columns = [
      {
        key: 'name',
        dataKey: 'name',
        title: __('company-tags.editor.name', {}, 'Name'),
        width: 300,
        flexGrow: 1,
        cellRenderer: ({ cellData }) => (
          <Tooltip text={cellData}>
            <span className={styles.itemName}>{cellData}</span>
          </Tooltip>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 64,
        flexShrink: 0,
        align: 'right',
        cellRenderer: ({ rowData }) => (
          <Dropdown
            portal
            top={28}
            right={0}
            name={`company-tag-${rowData.id}`}
            wrapperClassName={styles.actionsDropdown}
            header={
              <div className={styles.actionsHeader}>
                {__('content.tooltip.table.rowactions')}
              </div>
            }
            content={
              <div className={styles.actionsContent}>
                <button
                  type='button'
                  onClick={() => {
                    hideDropdown();
                    this.editTag(rowData);
                  }}
                >
                  <EditIcon />
                  {__('button.edit')}
                </button>
                <button
                  type='button'
                  className={styles.deleteAction}
                  onClick={() => {
                    hideDropdown();
                    this.deleteTag(rowData);
                  }}
                >
                  <DeleteIcon />
                  {__('button.delete')}
                </button>
              </div>
            }
          >
            <MenuHIcon className={styles.menuIcon} />
          </Dropdown>
        ),
      },
    ];

    return (
      <div className={styles.wrapper}>
        <TitleBar
          toolbar
          title={__('submenu.title.tags')}
          dropdownHeader={__('topbar.account.company-settings')}
          loading={loading}
        />
        <section className={styles.content}>
          <div className={styles.table}>
            <AutoSizer>
              {({ height, width }) => (
                <BaseTable
                  className={styles.baseTable}
                  columns={columns}
                  data={tags}
                  width={width}
                  height={height}
                  headerHeight={34}
                  headerClassName={styles.tableHeader}
                  rowHeight={56}
                  rowKey='id'
                  rowClassName={styles.tableRow}
                  emptyRenderer={
                    <div className={styles.emptyState}>
                      {loading
                        ? ''
                        : error
                          ? __('company-tags.load-failed')
                          : __('company-tags.empty')}
                    </div>
                  }
                />
              )}
            </AutoSizer>
          </div>
        </section>
      </div>
    );
  }
}

export default CompanyTags;
